/**
 * Serialize/deserialize helpers for the JSON-string wire formats the
 * legacy webhook endpoints expect.
 */

export interface KeyValueRow {
  key: string;
  value: string;
}

/** Object → array rows, with one empty placeholder if empty. */
export function objectToRows(obj: Record<string, unknown> | undefined | null): KeyValueRow[] {
  if (!obj || Object.keys(obj).length === 0) return [{ key: '', value: '' }];
  return Object.entries(obj).map(([k, v]) => ({
    key: k,
    value: typeof v === 'string' ? v : JSON.stringify(v),
  }));
}

/** Rows → object, dropping rows whose key is empty. */
export function rowsToObject(rows: KeyValueRow[]): Record<string, string> {
  return rows.reduce<Record<string, string>>((acc, { key, value }) => {
    if (key.trim()) acc[key] = value ?? '';
    return acc;
  }, {});
}

/** Parse a JSON-string header map, returning {} on any error. */
export function parseHeaderJson(raw: string | undefined | null): Record<string, string> {
  if (!raw || !raw.trim()) return {};
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return Object.fromEntries(
        Object.entries(parsed).map(([k, v]) => [
          k,
          typeof v === 'string' ? v : JSON.stringify(v),
        ]),
      );
    }
    return {};
  } catch {
    return {};
  }
}

/** Stringify a header/body map for the wire. Returns `'{}'` for empty maps. */
export function stringifyMap(map: Record<string, string>): string {
  return JSON.stringify(map);
}

/* ---------------- Voice body params ---------------- */

export interface VoiceBodyRow {
  id: number;
  parameter: string;
  /** Standard params: bare name. Custom params: free-form value. */
  value: string;
  selected: boolean;
  custom?: boolean;
}

/**
 * Build the initial bodyTable used by AddVoiceWebhook — all standard params
 * pre-selected, each row carries its own placeholder of `{paramName}` so the
 * serialized output matches the legacy wire format `{ key: '{key}' }`.
 */
export function buildInitialBodyTable(
  standardParams: { parameterName: string }[],
): VoiceBodyRow[] {
  return standardParams.map((p, idx) => ({
    id: idx + 1,
    parameter: p.parameterName,
    value: p.parameterName,
    selected: true,
  }));
}

/**
 * Rehydrate the bodyTable from a saved webhook's bodyJson, preserving any
 * custom rows the user added on top of the standard parameters.
 */
export function rehydrateBodyTable(
  savedBodyJson: string | undefined | null,
  standardParams: { parameterName: string }[],
): VoiceBodyRow[] {
  const saved = parseHeaderJson(savedBodyJson);
  const standardNames = new Set(standardParams.map((p) => p.parameterName));

  // Map "where each saved entry came from".
  const standardLabelByValue: Record<string, string> = {};
  const extras: Array<{ parameter: string; value: string }> = [];

  for (const [k, v] of Object.entries(saved)) {
    const stripped = v.replace(/[{}]/g, '');
    if (standardNames.has(stripped)) {
      standardLabelByValue[stripped] = k;
    } else {
      extras.push({ parameter: k, value: stripped });
    }
  }

  const table: VoiceBodyRow[] = standardParams.map((p, idx) => ({
    id: idx + 1,
    parameter: standardLabelByValue[p.parameterName] ?? p.parameterName,
    value: p.parameterName,
    selected: Boolean(standardLabelByValue[p.parameterName]),
  }));

  let nextId = table.length + 1;
  for (const ex of extras) {
    table.push({
      id: nextId++,
      parameter: ex.parameter,
      value: ex.value,
      selected: true,
      custom: true,
    });
  }

  return table;
}

/**
 * Serialize a bodyTable to the legacy wire format:
 *   standard row → { parameter: "{value}" }
 *   custom   row → { parameter: value }
 * Only selected rows contribute.
 */
export function bodyTableToWire(rows: VoiceBodyRow[]): Record<string, string> {
  return rows.reduce<Record<string, string>>((acc, r) => {
    if (!r.selected) return acc;
    acc[r.parameter] = r.custom ? r.value : `{${r.value}}`;
    return acc;
  }, {});
}
